import { useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Sector, Tooltip } from "recharts";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import { Paper, Typography, useTheme } from "@mui/material";
import { useChartPalette, getFormattedValue } from "../utils/chartUtils";
import { ViewByClaimType } from "@/types";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { convertCamelCaseToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

type StatusStat = {
  name: string;
  value: {
    claims: number;
    amount: number;
  };
};

type Props = {
  data: StatusStat[];
  viewBy: ViewByClaimType;
  isFullscreen: boolean;
};

const SliceTooltip = ({
  active,
  payload,
  totalClaims,
  totalAmount,
}: {
  active?: boolean;
  payload?: { payload: StatusStat }[];
  totalClaims: number;
  totalAmount: number;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const claimsPct = totalClaims ? ((p.value.claims / totalClaims) * 100).toFixed(1) : "0";
  const amountPct = totalAmount ? ((p.value.amount / totalAmount) * 100).toFixed(1) : "0";
  return (
    <Paper elevation={3} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{convertCamelCaseToTitleCase(p.name)}</Typography>
      <Typography variant="body2">
        {p.value.claims.toLocaleString()} claims ({claimsPct}%)
      </Typography>
      <Typography variant="body2">
        {formatUSD(p.value.amount)} ({amountPct}%)
      </Typography>
    </Paper>
  );
};

// Hover "size-up": the active sector grows outward and gains a thin outer ring.
const renderActiveShape = (props: PieSectorDataItem) => {
  const { cx, cy, innerRadius, outerRadius = 0, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cornerRadius={4}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 10}
        outerRadius={outerRadius + 13}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.4}
      />
    </g>
  );
};

const ClaimsByStatusChart = ({ data, isFullscreen, viewBy }: Props) => {
  const chartPalette = useChartPalette();
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const isAmountViewByClaimType = viewBy === ViewByClaimType.CLAIM_AMOUNT;
  const dataKey = isAmountViewByClaimType ? "amount" : "claims";

  if (!data.length) return <EmptyChartState />;

  const totalClaims = data.reduce((sum, d) => sum + d.value.claims, 0);
  const totalAmount = data.reduce((sum, d) => sum + d.value.amount, 0);

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <PieChart>
        <defs>
          {data.map((_entry, index) => {
            const c = chartPalette[index % chartPalette.length];
            return (
              <linearGradient key={`grad-${index}`} id={`statusGrad${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity={1} />
                <stop offset="100%" stopColor={c} stopOpacity={0.6} />
              </linearGradient>
            );
          })}
        </defs>
        <Pie
          data={data}
          dataKey={`value.${dataKey}`}
          nameKey="name"
          cx="50%"
          cy="50%"
          startAngle={90}
          endAngle={-270}
          innerRadius={isFullscreen ? "50%" : "45%"}
          outerRadius={isFullscreen ? "76%" : "68%"}
          paddingAngle={2}
          cornerRadius={4}
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          onMouseEnter={(_, index) => setActiveIndex(index)}
          onMouseLeave={() => setActiveIndex(undefined)}
          label={({ payload }) =>
            `${convertCamelCaseToTitleCase(payload.name)}: ${getFormattedValue(payload.value[dataKey], viewBy)}`
          }
        >
          {data.map((_entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={`url(#statusGrad${index})`}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
            />
          ))}
        </Pie>
        <Tooltip
          content={(props) => (
            <SliceTooltip
              active={props.active}
              payload={props.payload as unknown as { payload: StatusStat }[] | undefined}
              totalClaims={totalClaims}
              totalAmount={totalAmount}
            />
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default ClaimsByStatusChart;
